"use client";

import Link from "next/link";
import { readBoundedResponseBytes } from "@/lib/http/response-body";
import type { Route } from "next";
import { useEffect, useRef, useState } from "react";

import { event as gaEvent } from "@/lib/gtag";
import { trackJourneyEvent } from "@/lib/journey-analytics";
import { BLOG_SEARCH_MIN_QUERY_LENGTH } from "@/lib/blog/search-constants";
import { normalizeBlogSearchText } from "@/lib/blog/search-lexical";
import { setPendingBlogSearchResultOrigin } from "@/lib/blog/search-navigation";

const HOME_RESULT_LIMIT = 3;
const HOME_RESULT_DEBOUNCE_MS = 300;
const HOME_RESULT_REQUEST_TIMEOUT_MS = 8_000;

type HomeSearchResultPost = {
  slug: string;
  href: string;
  title: string;
  description: string;
  category?: {
    label: string;
    slug: string;
  };
};

type HomeSearchResultPayload = {
  normalizedQuery: string;
  queryLength: number;
  searchBucket: string;
  resultCount: number;
  totalMatchCount: number;
  posts: HomeSearchResultPost[];
  requestId?: string;
};

type HomeSearchResultsProps = {
  query: string;
  analyticsPlacement?: "home_header" | "blog_hero";
  analyticsTool?: "home_search" | "blog_search";
  searchInputMode?: "free_text" | "guided_chips";
  enabled?: boolean;
  searchAttemptId?: string;
  onAttemptOutcome?: (
    outcome: "result_selected" | "view_all_selected" | "zero_results",
    selection?: { resultPosition: number; targetId: string }
  ) => void;
};

type ResultState =
  | { status: "idle"; payload: null }
  | { status: "loading"; payload: HomeSearchResultPayload | null }
  | { status: "success"; payload: HomeSearchResultPayload }
  | { status: "error"; payload: null };

function asNonNegativeInteger(value: unknown): number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : 0;
}

function normalizeResultPost(value: unknown): HomeSearchResultPost | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const post = value as Record<string, unknown>;
  if (
    typeof post.slug !== "string" ||
    !post.slug.trim() ||
    typeof post.href !== "string" ||
    !post.href.startsWith("/blog/") ||
    typeof post.title !== "string" ||
    !post.title.trim()
  ) {
    return null;
  }

  const category =
    post.category !== null &&
    typeof post.category === "object" &&
    !Array.isArray(post.category)
      ? (post.category as Record<string, unknown>)
      : null;
  const normalizedCategory =
    category &&
    typeof category.label === "string" &&
    category.label.trim() &&
    typeof category.slug === "string" &&
    category.slug.trim()
      ? { label: category.label.trim(), slug: category.slug.trim() }
      : undefined;

  return {
    slug: post.slug.trim(),
    href: post.href,
    title: post.title.trim(),
    description:
      typeof post.description === "string" ? post.description.trim() : "",
    ...(normalizedCategory ? { category: normalizedCategory } : {}),
  };
}

async function fetchHomeSearchResults({
  query,
  signal,
}: {
  query: string;
  signal: AbortSignal;
}): Promise<HomeSearchResultPayload> {
  const response = await fetch("/api/search/results", {
    method: "POST",
    body: JSON.stringify({ q: query, limit: HOME_RESULT_LIMIT }),
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Home search results failed (${response.status})`);
  }

  const body = await readBoundedResponseBytes(response, 256 * 1024, signal);
  if (!body.ok) throw new Error("Home search response could not be read");
  const rawPayload: unknown = JSON.parse(new TextDecoder().decode(body.bytes));
  if (
    !rawPayload ||
    typeof rawPayload !== "object" ||
    Array.isArray(rawPayload)
  ) {
    throw new Error("Home search response was malformed");
  }
  const payload = rawPayload as Record<string, unknown>;
  if (!Array.isArray(payload.posts))
    throw new Error("Home search posts were malformed");
  const posts = Array.isArray(payload.posts)
    ? payload.posts
        .map(normalizeResultPost)
        .filter((post): post is HomeSearchResultPost => post !== null)
        .slice(0, HOME_RESULT_LIMIT)
    : [];
  if (payload.posts.length > 0 && posts.length === 0) {
    throw new Error("Home search posts were malformed");
  }
  const requestId = response.headers.get("X-Search-Request-Id") ?? "";
  return {
    normalizedQuery:
      typeof payload.normalizedQuery === "string"
        ? payload.normalizedQuery
        : "",
    queryLength: asNonNegativeInteger(payload.queryLength),
    searchBucket:
      typeof payload.searchBucket === "string" && payload.searchBucket.trim()
        ? payload.searchBucket
        : "general",
    resultCount: asNonNegativeInteger(payload.resultCount),
    totalMatchCount: asNonNegativeInteger(payload.totalMatchCount),
    posts,
    requestId: /^search-request-[A-Za-z0-9-]{1,128}$/.test(requestId)
      ? requestId
      : undefined,
  };
}

export default function HomeSearchResults({
  query,
  analyticsPlacement = "home_header",
  analyticsTool = "home_search",
  searchInputMode = "free_text",
  enabled = true,
  searchAttemptId,
  onAttemptOutcome,
}: HomeSearchResultsProps) {
  const [state, setState] = useState<ResultState>({
    status: "idle",
    payload: null,
  });
  const lastTrackedSignatureRef = useRef<string | null>(null);
  const normalizedQuery = normalizeBlogSearchText(query);
  const analyticsSource =
    analyticsTool === "blog_search"
      ? "blog_inline_results"
      : "home_inline_results";
  const resultOrigin =
    analyticsTool === "blog_search"
      ? "blog_live_debounce"
      : "home_live_debounce";

  useEffect(() => {
    if (!enabled || normalizedQuery.length < BLOG_SEARCH_MIN_QUERY_LENGTH) {
      setState({ status: "idle", payload: null });
      lastTrackedSignatureRef.current = null;
      return;
    }

    const controller = new AbortController();
    let requestTimedOut = false;
    let requestTimeout: number | null = null;
    const timeout = window.setTimeout(async () => {
      setState((previous) => ({
        status: "loading",
        payload: previous.payload,
      }));

      try {
        const payload = await Promise.race([
          fetchHomeSearchResults({
            query,
            signal: controller.signal,
          }),
          new Promise<never>((_, reject) => {
            if (requestTimeout !== null) window.clearTimeout(requestTimeout);
            requestTimeout = window.setTimeout(() => {
              requestTimedOut = true;
              controller.abort();
              reject(new Error("Home search results request timed out"));
            }, HOME_RESULT_REQUEST_TIMEOUT_MS);
          }),
        ]);
        if (controller.signal.aborted) return;
        setState({ status: "success", payload });
      } catch {
        if (controller.signal.aborted && !requestTimedOut) return;
        setState({ status: "error", payload: null });
      } finally {
        if (requestTimeout !== null) {
          window.clearTimeout(requestTimeout);
          requestTimeout = null;
        }
      }
    }, HOME_RESULT_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      if (requestTimeout !== null) window.clearTimeout(requestTimeout);
      controller.abort();
    };
  }, [enabled, normalizedQuery, query]);

  const payload = state.payload;
  const posts = payload?.posts ?? [];
  const hasResults = posts.length > 0;

  useEffect(() => {
    if (state.status !== "success" || !payload) return;

    const signature = [
      payload.normalizedQuery,
      payload.searchBucket,
      payload.totalMatchCount,
    ].join("|");
    if (lastTrackedSignatureRef.current === signature) return;
    lastTrackedSignatureRef.current = signature;

    gaEvent("tool_search_result", {
      tool: analyticsTool,
      placement: analyticsPlacement,
      query_length: payload.queryLength,
      search_bucket: payload.searchBucket,
      result_count: payload.totalMatchCount,
      zero_results: payload.totalMatchCount === 0,
      source: analyticsSource,
      result_origin: resultOrigin,
      search_input_mode: searchInputMode,
      search_ui_variant: "progressive_v1",
      visible_result_count: payload.posts.length,
    });
    trackJourneyEvent("search_attempt", {
      action: "results_visible",
      journey_name: "home_search_attempt",
      query_length: payload.queryLength,
      result_count: payload.totalMatchCount,
      search_attempt_id: searchAttemptId,
      search_bucket: payload.searchBucket,
      search_input_mode: searchInputMode,
      search_query: query.trim(),
      search_request_id: payload.requestId,
      search_ui_variant: "progressive_v1",
      tool: analyticsTool,
      visible_result_count: payload.posts.length,
    });
    if (payload.totalMatchCount === 0) {
      onAttemptOutcome?.("zero_results");
    }
  }, [
    analyticsPlacement,
    analyticsSource,
    analyticsTool,
    payload,
    query,
    resultOrigin,
    searchAttemptId,
    searchInputMode,
    state.status,
    onAttemptOutcome,
  ]);

  if (state.status === "idle") {
    return null;
  }

  return (
    <section
      className="mt-4 rounded-2xl border border-white/70 bg-white/95 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.14)] ring-1 ring-white/50"
      aria-label="Search results"
      aria-live="polite"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-semibold text-slate-900">
          {state.status === "loading" && !payload
            ? "Searching blog posts"
            : state.status === "error"
            ? "Search results are temporarily unavailable"
            : payload && payload.totalMatchCount > 0
            ? "Top matches"
            : "No matching blog posts yet"}
        </p>
        {payload && payload.totalMatchCount > HOME_RESULT_LIMIT && (
          <Link<string>
            href={`/blog?q=${encodeURIComponent(query.trim())}` as Route}
            className="text-sm font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            data-ga-action="view_all_search_results"
            data-ga-destination-path="/blog"
            data-ga-element-id="home-search-view-all-results"
            data-ga-event="tool_search_click"
            data-ga-query-length={String(payload.queryLength)}
            data-ga-result-origin={resultOrigin}
            data-ga-search-bucket={payload.searchBucket}
            data-ga-search-input-mode={searchInputMode}
            data-ga-search-ui-variant="progressive_v1"
            data-ga-source={analyticsSource}
            data-ga-tool={analyticsTool}
            onClick={() => {
              const href = `/blog?q=${encodeURIComponent(query.trim())}`;
              setPendingBlogSearchResultOrigin({
                href,
                searchAttemptId,
                searchInputMode,
                source: "home_view_all",
              });
              onAttemptOutcome?.("view_all_selected");
            }}
          >
            View all results
          </Link>
        )}
      </div>

      {hasResults && (
        <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post, index) => (
            <li key={post.slug}>
              <Link<string>
                href={post.href as Route}
                className="block h-full rounded-xl border border-slate-200 bg-white p-3.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
                data-ga-action="open_search_result"
                data-ga-category="blog"
                data-ga-event="tool_search_click"
                data-ga-element-id={`home-search-result-${post.slug}`}
                data-ga-destination-path={post.href}
                data-ga-label={post.category?.slug}
                data-ga-query-length={String(payload?.queryLength ?? 0)}
                data-ga-result-origin={resultOrigin}
                data-ga-result-rank={String(index + 1)}
                data-ga-search-bucket={payload?.searchBucket}
                data-ga-search-input-mode={searchInputMode}
                data-ga-search-ui-variant="progressive_v1"
                data-ga-source={analyticsSource}
                data-ga-tool={analyticsTool}
                data-ga-value={post.slug}
                onClick={() =>
                  onAttemptOutcome?.("result_selected", {
                    resultPosition: index + 1,
                    targetId: post.slug,
                  })
                }
              >
                {post.category && (
                  <span className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                    {post.category.label}
                  </span>
                )}
                <h2 className="mt-1 line-clamp-2 text-sm font-semibold leading-5 text-slate-950">
                  {post.title}
                </h2>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {state.status === "loading" && payload && (
        <p className="mt-3 text-xs text-slate-500">Updating results...</p>
      )}
    </section>
  );
}
